import React from 'react';
import styled from 'styled-components';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { IconButton } from 'design-system/src/components/Button/Button';
import Copy from 'design-system/src/assets/icons/copy.svg?react';
import PrescriptionIcon from '../../assets/prescription.svg?react';
import toast from 'react-hot-toast';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { TANDEM_PHARMACY_INFO } from '../../constants';

const Container = styled.div`
  border-radius: 8px;
  border: 1px solid var(--surface-200);
  background: var(--surface-100);
  display: flex;
  width: 642px;
  padding: 12px 20px;
  flex-direction: column;
  align-items: flex-start;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-self: stretch;
  margin-bottom: 12px;
`;

const HeaderTitle = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: var(--grayscale-600);
  align-self: center;
  box-sizing: border-box;
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const DetailRow = styled.div<{ $isLast?: boolean }>`
  display: flex;
  padding: 2px 0;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  ${({ $isLast }) => !$isLast && 'border-bottom: 1px solid var(--grayscale-100);'}
`;

const LabelColumn = styled.div`
  width: 120px;
  display: flex;
  align-items: center;
`;

const ValueColumn = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
`;

const StyledIconButton = styled(IconButton)`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export type TandemPharmacyDetailsProps = {
  /**
   * Class name for additional styling; used to pass down styles when using inheritance in styled components
   */
  className?: string;
};

/**
 * TandemPharmacyDetails component displays Tandem pharmacy information with copy functionality.
 */
export const TandemPharmacyDetails = ({ className }: TandemPharmacyDetailsProps) => {
  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value).then(
      () => {
        toast.custom(() => <Toast variant="success">Copied!</Toast>);
      },
      () => {
        toast.custom(() => <StandardErrorToast />);
      },
    );
  };

  return (
    <Container className={className}>
      <HeaderContainer>
        <PrescriptionIcon />
        <HeaderTitle styledAs="bodyExtraSmallSpaceGrotesk" weight={500}>
          Tandem Pharmacy Details
        </HeaderTitle>
      </HeaderContainer>

      <Divider />

      <Table role="table" aria-label="Tandem Pharmacy Information">
        <DetailRow role="row">
          <LabelColumn role="rowheader">
            <Typography weight={600}>Name</Typography>
          </LabelColumn>
          <ValueColumn role="cell">
            <Typography color="var(--grayscale-600)">{TANDEM_PHARMACY_INFO.name}</Typography>
            <StyledIconButton onClick={() => handleCopy(TANDEM_PHARMACY_INFO.name)}>
              <Copy title="copy pharmacy name to clipboard" />
            </StyledIconButton>
          </ValueColumn>
        </DetailRow>

        <DetailRow role="row">
          <LabelColumn role="rowheader">
            <Typography weight={600}>Address</Typography>
          </LabelColumn>
          <ValueColumn role="cell">
            <Typography color="var(--grayscale-600)">{TANDEM_PHARMACY_INFO.address}</Typography>
            <StyledIconButton onClick={() => handleCopy(TANDEM_PHARMACY_INFO.address)}>
              <Copy title="copy pharmacy address to clipboard" />
            </StyledIconButton>
          </ValueColumn>
        </DetailRow>

        <DetailRow $isLast role="row">
          <LabelColumn role="rowheader">
            <Typography weight={600}>NCPDP</Typography>
          </LabelColumn>
          <ValueColumn role="cell">
            <Typography color="var(--grayscale-600)">{TANDEM_PHARMACY_INFO.ncpdp}</Typography>
            <StyledIconButton onClick={() => handleCopy(TANDEM_PHARMACY_INFO.ncpdp)}>
              <Copy title="copy NCPDP number to clipboard" />
            </StyledIconButton>
          </ValueColumn>
        </DetailRow>
      </Table>
    </Container>
  );
};

export default TandemPharmacyDetails;
